import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Package,
  Plus,
  Image,
  X,
  CheckCircle,
  XCircle,
  Loader,
  Building2,
  Trash2
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getBrands,
  getSizes,
  getUseCases,
  getTileTypes,
  createBrand,
  createSize,
  createUseCase,
  createTileType,
  deleteBrand,
  deleteSize,
  deleteUseCase,
  deleteTileType,
  getAccessoryCategories
} from '../api/categories';
import { getProductById, createProduct, updateProduct, checkProductCode } from '../api/products';
import { getBranches, getBranchMode } from '../api/branches';
import { useAuth } from '../contexts/AuthContext';
import QuickAddCategoryModal from '../components/QuickAddCategoryModal';
import LoadingSpinner from '../components/common/LoadingSpinner';

const ProductFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Branch mode and branches
  const [isSingleBranch, setIsSingleBranch] = useState(true);
  const [branches, setBranches] = useState([]);
  const [branchAllocations, setBranchAllocations] = useState([]); // [{ branchId, quantity }]

  // Product code checking
  const [checkingCode, setCheckingCode] = useState(false);
  const [codeExists, setCodeExists] = useState(false);
  const [existingProduct, setExistingProduct] = useState(null);
  const codeCheckTimeout = useRef(null);

  // Categories
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [useCases, setUseCases] = useState([]);
  const [tileTypes, setTileTypes] = useState([]);
  const [accessoryCategories, setAccessoryCategories] = useState([]);

  // Image upload states
  const [productImageFiles, setProductImageFiles] = useState([]);
  const [modelImageFiles, setModelImageFiles] = useState([]);
  const [productImagePreviews, setProductImagePreviews] = useState([]);
  const [modelImagePreviews, setModelImagePreviews] = useState([]);
  const [existingProductImages, setExistingProductImages] = useState([]);
  const [existingModelImages, setExistingModelImages] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    product_code: '',
    name: '',
    description: '',
    product_type: 'tile',
    brand_id: '',
    size_id: '',
    use_case_id: '',
    type_id: '',
    accessory_category_id: '',
    cost_price: '',
    selling_price: '',
    commission_amount: '', // Commission per unit for referrer
    stock_quantity: '',
    min_stock_level: '',
    unit: 'cartons', // Default unit for tiles
    is_active: true,
    is_featured: false
  });

  const [errors, setErrors] = useState({});

  // Modal states for quick-add categories
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [showUseCaseModal, setShowUseCaseModal] = useState(false);
  const [showTileTypeModal, setShowTileTypeModal] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const [
        brandsRes,
        sizesRes,
        useCasesRes,
        typesRes,
        accessoryCatRes,
        branchModeRes,
        branchesRes
      ] = await Promise.all([
        getBrands(),
        getSizes(),
        getUseCases(),
        getTileTypes(),
        getAccessoryCategories(),
        getBranchMode().catch(() => ({ isSingleBranch: true })),
        getBranches({ isActive: true }).catch(() => ({ data: [] }))
      ]);

      setBrands(brandsRes || []);
      setSizes(sizesRes || []);
      setUseCases(useCasesRes || []);
      setTileTypes(typesRes || []);
      setAccessoryCategories(accessoryCatRes?.data || accessoryCatRes || []);
      setIsSingleBranch(branchModeRes?.data?.singleBranchMode !== false);
      setBranches(branchModeRes?.data?.branches || branchesRes.data || branchesRes || []);
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const product = await getProductById(id);

      setFormData({
        product_code: product.product_code || '',
        name: product.name || '',
        description: product.description || '',
        product_type: product.product_type || 'tile',
        brand_id: product.brand_id || '',
        size_id: product.size_id || '',
        use_case_id: product.use_case_id || '',
        type_id: product.type_id || '',
        accessory_category_id: product.accessory_category_id || '',
        cost_price: product.cost_price || '',
        selling_price: product.selling_price || '',
        commission_amount: product.commission_amount || '',
        stock_quantity: product.stock_quantity || '',
        min_stock_level: product.min_stock_level || '',
        unit: product.unit || 'boxes',
        is_active: product.is_active !== false,
        is_featured: product.is_featured || false
      });

      // Populate existing images
      if (product.images) {
        if (typeof product.images === 'object' && !Array.isArray(product.images)) {
          setExistingProductImages(
            Array.isArray(product.images.productImages) ? product.images.productImages : []
          );
          setExistingModelImages(
            Array.isArray(product.images.modelImages) ? product.images.modelImages : []
          );
        } else if (Array.isArray(product.images)) {
          // Legacy support: assume all are product images
          setExistingProductImages(product.images);
        }
      }
    } catch (err) {
      toast.error('Failed to load product');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      fetchProduct();
    }
  }, [isEditMode, fetchCategories, fetchProduct]);

  // Handle brand deletion
  const handleBrandDelete = async (id) => {
    await fetchCategories();
  };

  // Handle size deletion
  const handleSizeDelete = async (id) => {
    await fetchCategories();
  };

  // Handle use case deletion
  const handleUseCaseDelete = async (id) => {
    await fetchCategories();
  };

  // Handle tile type deletion
  const handleTileTypeDelete = async (id) => {
    await fetchCategories();
  };

  // Check product code existence
  const checkCode = async (code) => {
    if (!code || code.trim() === '' || isEditMode) {
      setCodeExists(false);
      setExistingProduct(null);
      return;
    }

    try {
      setCheckingCode(true);
      const result = await checkProductCode(code.trim().toUpperCase());
      setCodeExists(result.exists);
      setExistingProduct(result.exists ? result.product : null);
    } catch (err) {
      setCodeExists(false);
      setExistingProduct(null);
    } finally {
      setCheckingCode(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Check product code with debounce
    if (name === 'product_code' && !isEditMode) {
      // Clear existing timeout
      if (codeCheckTimeout.current) {
        clearTimeout(codeCheckTimeout.current);
      }

      // Set new timeout to check code after 500ms
      codeCheckTimeout.current = setTimeout(() => {
        checkCode(value);
      }, 500);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (codeCheckTimeout.current) {
        clearTimeout(codeCheckTimeout.current);
      }
    };
  }, []);

  // Handle image file selection for product images
  const handleProductImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Create preview URLs
    const newPreviews = files.map((file) => URL.createObjectURL(file));

    setProductImageFiles((prev) => [...prev, ...files]);
    setProductImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  // Handle image file selection for model images
  const handleModelImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Create preview URLs
    const newPreviews = files.map((file) => URL.createObjectURL(file));

    setModelImageFiles((prev) => [...prev, ...files]);
    setModelImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  // Remove product image preview
  const removeProductImage = (index) => {
    setProductImageFiles((prev) => prev.filter((_, i) => i !== index));
    setProductImagePreviews((prev) => {
      const newPreviews = prev.filter((_, i) => i !== index);
      // Revoke the URL to free memory
      URL.revokeObjectURL(prev[index]);
      return newPreviews;
    });
  };

  // Remove model image preview
  const removeModelImage = (index) => {
    setModelImageFiles((prev) => prev.filter((_, i) => i !== index));
    setModelImagePreviews((prev) => {
      const newPreviews = prev.filter((_, i) => i !== index);
      // Revoke the URL to free memory
      URL.revokeObjectURL(prev[index]);
      return newPreviews;
    });
  };

  // Remove existing product image
  const removeExistingProductImage = (index) => {
    setExistingProductImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove existing model image
  const removeExistingModelImage = (index) => {
    setExistingModelImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Cleanup preview URLs on unmount and when previews change
  useEffect(() => {
    return () => {
      productImagePreviews.forEach((url) => URL.revokeObjectURL(url));
      modelImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [productImagePreviews, modelImagePreviews]);

  // Handle successful category creation
  const handleBrandCreated = async (result) => {
    await fetchCategories();
    setFormData((prev) => ({ ...prev, brand_id: result.id }));
    toast.success('Brand added and selected');
  };

  const handleSizeCreated = async (result) => {
    await fetchCategories();
    setFormData((prev) => ({ ...prev, size_id: result.id }));
    toast.success('Size added and selected');
  };

  const handleUseCaseCreated = async (result) => {
    await fetchCategories();
    setFormData((prev) => ({ ...prev, use_case_id: result.id }));
    toast.success('Use case added and selected');
  };

  const handleTileTypeCreated = async (result) => {
    await fetchCategories();
    setFormData((prev) => ({ ...prev, type_id: result.id }));
    toast.success('Tile type added and selected');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.product_code || formData.product_code.trim() === '') {
      newErrors.product_code = 'Product code is required';
    } else if (!isEditMode && codeExists) {
      newErrors.product_code = 'This product code already exists';
    }

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Product name is required';
    }

    if (!formData.product_type) {
      newErrors.product_type = 'Product type is required';
    }

    // Tile-specific validations (only for tiles)
    if (formData.product_type === 'tile') {
      if (!formData.brand_id) {
        newErrors.brand_id = 'Brand is required for tiles';
      }
      if (!formData.size_id) {
        newErrors.size_id = 'Size is required for tiles';
      }
      if (!formData.type_id) {
        newErrors.type_id = 'Tile type is required';
      }
    }

    // Accessory-specific validations (only for accessories)
    if (formData.product_type === 'accessory') {
      if (!formData.accessory_category_id) {
        newErrors.accessory_category_id = 'Accessory category is required';
      }
    }

    if (!formData.cost_price || parseFloat(formData.cost_price) <= 0) {
      newErrors.cost_price = 'Cost price must be greater than 0';
    }

    if (!formData.selling_price || parseFloat(formData.selling_price) <= 0) {
      newErrors.selling_price = 'Selling price must be greater than 0';
    }

    // Warn but don't block if selling price is below cost (negative margin is allowed)
    // The system already handles negative profit margins

    // Stock validation for new products
    if (!isEditMode) {
      if (!isSingleBranch && ['super_admin', 'system_admin', 'retail_admin'].includes(user?.role)) {
        // Multi-branch mode: require at least one branch with stock assigned
        const validAllocations = branchAllocations.filter(
          (a) => a.branchId && parseInt(a.quantity) > 0
        );
        if (validAllocations.length === 0) {
          newErrors.branchAllocations =
            'Please assign stock to at least one branch (select a branch and enter quantity above 0)';
        }
      } else {
        // Single-branch mode: stock_quantity can be 0 or empty (defaults to 0)
        const qty = formData.stock_quantity;
        if (qty !== '' && qty !== undefined && parseInt(qty) < 0) {
          newErrors.stock_quantity = 'Stock quantity cannot be negative';
        }
      }
    }

    // Min stock level can be 0
    const minLevel = formData.min_stock_level;
    if (minLevel === '' || minLevel === undefined || minLevel === null) {
      newErrors.min_stock_level = 'Minimum stock level is required';
    } else if (parseInt(minLevel) < 0) {
      newErrors.min_stock_level = 'Minimum stock level cannot be negative';
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      // Show the first specific error in the toast
      const firstError = Object.values(validationErrors)[0];
      toast.error(firstError);
      return;
    }

    try {
      setLoading(true);

      // Create FormData for multipart/form-data
      const formDataToSend = new FormData();

      // Append all common fields
      formDataToSend.append('productCode', formData.product_code.trim().toUpperCase());
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('productType', formData.product_type);
      formDataToSend.append('costPrice', parseFloat(formData.cost_price) || 0);
      formDataToSend.append('sellingPrice', parseFloat(formData.selling_price));
      formDataToSend.append('commissionAmount', parseFloat(formData.commission_amount) || 0);
      formDataToSend.append('stockQuantity', parseInt(formData.stock_quantity) || 0);
      formDataToSend.append('minStockLevel', parseInt(formData.min_stock_level));
      formDataToSend.append('unit', formData.unit);
      formDataToSend.append('isActive', formData.is_active);
      formDataToSend.append('isFeatured', formData.is_featured);

      // Append branch stock allocations for multi-branch mode (higher level admins)
      if (!isSingleBranch && ['super_admin', 'system_admin', 'retail_admin'].includes(user?.role)) {
        const validAllocations = branchAllocations.filter(
          (a) => a.branchId && parseInt(a.quantity) > 0
        );
        formDataToSend.append('branchStockAllocations', JSON.stringify(validAllocations));
      }

      // Append tile-specific fields (only for tiles)
      if (formData.product_type === 'tile') {
        formDataToSend.append('brandId', parseInt(formData.brand_id) || '');
        formDataToSend.append('sizeId', parseInt(formData.size_id) || '');
        formDataToSend.append(
          'useCaseId',
          formData.use_case_id ? parseInt(formData.use_case_id) : ''
        );
        formDataToSend.append('typeId', parseInt(formData.type_id) || '');
      }

      // Append accessory-specific fields (only for accessories)
      if (formData.product_type === 'accessory') {
        formDataToSend.append(
          'accessoryCategoryId',
          parseInt(formData.accessory_category_id) || ''
        );
      }

      // Append product images
      productImageFiles.forEach((file) => {
        formDataToSend.append('productImages', file);
      });

      // Append model images
      modelImageFiles.forEach((file) => {
        formDataToSend.append('modelImages', file);
      });

      // If editing, append existing images that weren't removed
      if (isEditMode) {
        formDataToSend.append('existingProductImages', JSON.stringify(existingProductImages));
        formDataToSend.append('existingModelImages', JSON.stringify(existingModelImages));
      }

      if (isEditMode) {
        await updateProduct(id, formDataToSend);
        toast.success('Product updated successfully');
      } else {
        await createProduct(formDataToSend);
        toast.success('Product created successfully');
      }

      navigate('/products');
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || err.response?.data?.message || 'Failed to save product';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const profitMargin =
    formData.cost_price && formData.selling_price
      ? (((formData.selling_price - formData.cost_price) / formData.cost_price) * 100).toFixed(2)
      : 0;

  const profitPerUnit =
    formData.selling_price && formData.cost_price
      ? (formData.selling_price - formData.cost_price).toFixed(2)
      : 0;

  if (loadingCategories) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>

        <div className="flex items-center gap-3">
          <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
            <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
              {isEditMode ? 'Edit Product' : 'Create New Product'}
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              {isEditMode ? 'Update product information' : 'Add a new product to inventory'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-md p-4 sm:p-6 space-y-4 sm:space-y-6"
      >
        {/* Product Code */}
        <div>
          <label htmlFor="product_code" className="block text-sm font-medium text-gray-700 mb-1">
            Product Code (Tile/Accessory) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              id="product_code"
              name="product_code"
              value={formData.product_code}
              onChange={handleChange}
              disabled={loading || isEditMode}
              className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                errors.product_code || codeExists
                  ? 'border-red-300'
                  : formData.product_code && !codeExists && !checkingCode && !isEditMode
                    ? 'border-green-300'
                    : 'border-gray-300'
              } ${isEditMode ? 'bg-gray-100' : ''}`}
              placeholder="e.g., 40001, 25154, 66232 or PROD-001"
            />
            {!isEditMode && formData.product_code && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                {checkingCode ? (
                  <Loader className="w-5 h-5 text-gray-400 animate-spin" />
                ) : codeExists ? (
                  <XCircle className="w-5 h-5 text-red-500" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
              </div>
            )}
          </div>
          {errors.product_code && (
            <p className="mt-1 text-sm text-red-600">{errors.product_code}</p>
          )}
          {!errors.product_code && !isEditMode && codeExists && existingProduct && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800 font-medium mb-1">
                ⚠️ Product code already exists:
              </p>
              <p className="text-sm text-red-700">
                <span className="font-semibold">{existingProduct.name}</span>
                <br />
                <span className="text-xs">
                  Brand: {existingProduct.brand_name} | Size: {existingProduct.size_dimension}
                </span>
              </p>
            </div>
          )}
          {!errors.product_code &&
            !isEditMode &&
            formData.product_code &&
            !codeExists &&
            !checkingCode && (
              <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Product code is available
              </p>
            )}
          {isEditMode && (
            <p className="mt-1 text-xs text-gray-500">
              Product code cannot be changed after creation
            </p>
          )}
        </div>

        {/* Product Type */}
        <div>
          <label htmlFor="product_type" className="block text-sm font-medium text-gray-700 mb-1">
            Product Type <span className="text-red-500">*</span>
          </label>
          <select
            id="product_type"
            name="product_type"
            value={formData.product_type}
            onChange={handleChange}
            disabled={loading}
            className={`w-full px-3 py-2 border rounded-md shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 ${
              errors.product_type ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="tile">Tile</option>
            <option value="accessory">Accessory</option>
          </select>
          {errors.product_type && (
            <p className="mt-1 text-sm text-red-600">{errors.product_type}</p>
          )}
        </div>

        {/* Product Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Product Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={loading}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="e.g., Premium Polished Floor Tile"
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter product description..."
          />
        </div>

        {/* Accessory Category (only for accessories) */}
        {formData.product_type === 'accessory' && (
          <div>
            <label
              htmlFor="accessory_category_id"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Accessory Category <span className="text-red-500">*</span>
            </label>
            <select
              id="accessory_category_id"
              name="accessory_category_id"
              value={formData.accessory_category_id}
              onChange={handleChange}
              disabled={loading}
              className={`w-full px-3 py-2 border rounded-md shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 ${
                errors.accessory_category_id ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select Accessory Category</option>
              {accessoryCategories
                .filter((cat) => cat.is_active)
                .map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
            </select>
            {errors.accessory_category_id && (
              <p className="mt-1 text-sm text-red-600">{errors.accessory_category_id}</p>
            )}
          </div>
        )}

        {/* Tile Categories Grid (only for tiles) */}
        {formData.product_type === 'tile' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Brand */}
            <div>
              <label htmlFor="brand_id" className="block text-sm font-medium text-gray-700 mb-1">
                Brand <span className="text-red-500">*</span>
              </label>
              <select
                id="brand_id"
                name="brand_id"
                value={formData.brand_id}
                onChange={handleChange}
                disabled={loading}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.brand_id ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Brand</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
              {errors.brand_id && <p className="mt-1 text-sm text-red-600">{errors.brand_id}</p>}
            </div>

            {/* Size */}
            <div>
              <label htmlFor="size_id" className="block text-sm font-medium text-gray-700 mb-1">
                Size <span className="text-red-500">*</span>
              </label>
              <select
                id="size_id"
                name="size_id"
                value={formData.size_id}
                onChange={handleChange}
                disabled={loading}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.size_id ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Size</option>
                {sizes.map((size) => (
                  <option key={size.id} value={size.id}>
                    {size.dimension}
                  </option>
                ))}
              </select>
              {errors.size_id && <p className="mt-1 text-sm text-red-600">{errors.size_id}</p>}
            </div>

            {/* Use Case */}
            <div>
              <label htmlFor="use_case_id" className="block text-sm font-medium text-gray-700 mb-1">
                Use Case
              </label>
              <select
                id="use_case_id"
                name="use_case_id"
                value={formData.use_case_id}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Use Case</option>
                {useCases.map((useCase) => (
                  <option key={useCase.id} value={useCase.id}>
                    {useCase.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tile Type */}
            <div>
              <label htmlFor="type_id" className="block text-sm font-medium text-gray-700 mb-1">
                Tile Type <span className="text-red-500">*</span>
              </label>
              <select
                id="type_id"
                name="type_id"
                value={formData.type_id}
                onChange={handleChange}
                disabled={loading}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.type_id ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Type</option>
                {tileTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.type_id && <p className="mt-1 text-sm text-red-600">{errors.type_id}</p>}
            </div>
          </div>
        )}

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Cost Price */}
          <div>
            <label htmlFor="cost_price" className="block text-sm font-medium text-gray-700 mb-1">
              Cost Price (₦) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="cost_price"
              name="cost_price"
              value={formData.cost_price}
              onChange={handleChange}
              min="0"
              step="0.01"
              disabled={loading}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                errors.cost_price ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.cost_price && <p className="mt-1 text-sm text-red-600">{errors.cost_price}</p>}
          </div>

          {/* Selling Price */}
          <div>
            <label htmlFor="selling_price" className="block text-sm font-medium text-gray-700 mb-1">
              Selling Price (₦) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="selling_price"
              name="selling_price"
              value={formData.selling_price}
              onChange={handleChange}
              min="0"
              step="0.01"
              disabled={loading}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                errors.selling_price ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.selling_price && (
              <p className="mt-1 text-sm text-red-600">{errors.selling_price}</p>
            )}
          </div>

          {/* Commission Amount */}
          <div>
            <label
              htmlFor="commission_amount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Commission/Unit (₦)
            </label>
            <input
              type="number"
              id="commission_amount"
              name="commission_amount"
              value={formData.commission_amount}
              onChange={handleChange}
              min="0"
              step="0.01"
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.00"
            />
            <p className="mt-1 text-xs text-gray-500">Commission paid to referrer per unit sold</p>
          </div>
        </div>

        {/* Profit Display */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-secondary-lightGray">
          <div>
            <p
              className={`text-sm ${parseFloat(profitMargin) >= 0 ? 'text-green-700' : 'text-red-700'} mb-1`}
            >
              {parseFloat(profitMargin) >= 0 ? 'Profit Margin' : 'Loss Margin'}
            </p>
            <p
              className={`text-2xl font-bold ${parseFloat(profitMargin) >= 0 ? 'text-green-900' : 'text-red-900'}`}
            >
              {profitMargin}%
            </p>
          </div>
          <div>
            <p className={`text-sm ${profitPerUnit >= 0 ? 'text-green-700' : 'text-red-700'} mb-1`}>
              {profitPerUnit >= 0 ? 'Profit per Unit' : 'Loss per Unit'}
            </p>
            <p
              className={`text-2xl font-bold ${profitPerUnit >= 0 ? 'text-green-900' : 'text-red-900'}`}
            >
              ₦{Math.abs(profitPerUnit).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Stock Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Branch Stock Allocation (Create only) */}
          {!isEditMode && (
            <>
              {/* Multi-branch mode for higher level admins */}
              {!isSingleBranch &&
              ['super_admin', 'system_admin', 'retail_admin'].includes(user?.role) ? (
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Branch Stock Allocation
                    </div>
                  </label>
                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {branchAllocations.length === 0 && (
                      <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md border border-amber-200">
                        <strong>Required:</strong> Add at least one branch with stock allocation.
                      </div>
                    )}
                    {errors.branchAllocations && (
                      <p className="text-sm text-red-600">{errors.branchAllocations}</p>
                    )}
                    {branchAllocations.map((allocation, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <select
                          value={allocation.branchId}
                          onChange={(e) => {
                            const updated = [...branchAllocations];
                            updated[index].branchId = e.target.value;
                            setBranchAllocations(updated);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">Select Branch</option>
                          {branches.map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          value={allocation.quantity}
                          onChange={(e) => {
                            const updated = [...branchAllocations];
                            updated[index].quantity = e.target.value;
                            setBranchAllocations(updated);
                          }}
                          placeholder="Quantity"
                          min="0"
                          className="w-32 px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setBranchAllocations((prev) => prev.filter((_, i) => i !== index));
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        setBranchAllocations([...branchAllocations, { branchId: '', quantity: '' }])
                      }
                      className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Plus className="w-4 h-4" />
                      Add Branch
                    </button>
                  </div>
                  {errors.stock_quantity && (
                    <p className="mt-1 text-sm text-red-600">{errors.stock_quantity}</p>
                  )}
                </div>
              ) : (
                /* Single-branch mode OR branch_admin - simple stock input */
                <div>
                  <label
                    htmlFor="stock_quantity"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Initial Stock{' '}
                    {user?.role === 'branch_admin' && !isSingleBranch ? `(Your Branch)` : ''}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="stock_quantity"
                    name="stock_quantity"
                    value={formData.stock_quantity}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    disabled={loading}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors.stock_quantity ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="0"
                  />
                  {errors.stock_quantity && (
                    <p className="mt-1 text-sm text-red-600">{errors.stock_quantity}</p>
                  )}
                  {user?.role === 'branch_admin' && !isSingleBranch && (
                    <p className="mt-1 text-xs text-gray-500">
                      Stock will be added to your assigned branch
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Minimum Stock Level */}
          <div>
            <label
              htmlFor="min_stock_level"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Min Stock Level <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="min_stock_level"
              name="min_stock_level"
              value={formData.min_stock_level}
              onChange={handleChange}
              min="0"
              step="1"
              disabled={loading}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                errors.min_stock_level ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="0"
            />
            {errors.min_stock_level && (
              <p className="mt-1 text-sm text-red-600">{errors.min_stock_level}</p>
            )}
          </div>

          {/* Unit */}
          <div>
            <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-1">
              Unit
            </label>
            <select
              id="unit"
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="cartons">Cartons</option>
              <option value="pieces">Pieces</option>
              <option value="packs">Packs</option>
              <option value="bags">Bags</option>
              <option value="units">Units</option>
              <option value="liters">Liters</option>
              <option value="kg">Kilograms (kg)</option>
              <option value="meters">Meters</option>
              <option value="rolls">Rolls</option>
              <option value="boxes">Boxes</option>
              <option value="sets">Sets</option>
              <option value="tubes">Tubes</option>
              <option value="bottles">Bottles</option>
            </select>
          </div>
        </div>

        {/* Product Images Section */}
        <div className="border-t border-gray-200 pt-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
            <p className="text-xs text-gray-500 mb-3">
              Upload at least 2 different images of the product
            </p>

            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Image className="w-8 h-8 mb-2 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG, JPEG (MAX. 5MB each)</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleProductImageChange}
                  disabled={loading}
                />
              </label>
            </div>

            {/* Existing Product Images (Edit Mode) */}
            {isEditMode && existingProductImages.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Existing Images:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {existingProductImages.map((image, index) => (
                    <div key={`existing-product-${index}`} className="relative group">
                      <img
                        src={image.url || image}
                        alt={`Existing product ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingProductImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                        disabled={loading}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Product Image Previews */}
            {productImagePreviews.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">New Images to Upload:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {productImagePreviews.map((preview, index) => (
                    <div key={`product-preview-${index}`} className="relative group">
                      <img
                        src={preview}
                        alt={`Product preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeProductImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                        disabled={loading}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Model Images Section */}
        <div className="border-t border-gray-200 pt-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Model Images</label>
            <p className="text-xs text-gray-500 mb-3">
              Upload images showing how tiles look on buildings or in use
            </p>

            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Image className="w-8 h-8 mb-2 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG, JPEG (MAX. 5MB each)</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleModelImageChange}
                  disabled={loading}
                />
              </label>
            </div>

            {/* Existing Model Images (Edit Mode) */}
            {isEditMode && existingModelImages.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Existing Images:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {existingModelImages.map((image, index) => (
                    <div key={`existing-model-${index}`} className="relative group">
                      <img
                        src={image.url || image}
                        alt={`Existing model ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingModelImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                        disabled={loading}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Model Image Previews */}
            {modelImagePreviews.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">New Images to Upload:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {modelImagePreviews.map((preview, index) => (
                    <div key={`model-preview-${index}`} className="relative group">
                      <img
                        src={preview}
                        alt={`Model preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeModelImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                        disabled={loading}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Checkboxes */}
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              disabled={loading}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
              Active (Product will be visible and available for sale)
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_featured"
              name="is_featured"
              checked={formData.is_featured}
              onChange={handleChange}
              disabled={loading}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-700">
              Featured (Product will be highlighted)
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-end gap-3 pt-6 border-t border-gray-200">
          <Link
            to="/products"
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-h-[44px] flex items-center justify-center"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? 'Update Product' : 'Create Product'}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Quick Add Category Modals */}
      <QuickAddCategoryModal
        isOpen={showBrandModal}
        onClose={() => setShowBrandModal(false)}
        categoryType="brand"
        onSuccess={handleBrandCreated}
        createFunction={createBrand}
        existingItems={brands}
        deleteFunction={deleteBrand}
        onDelete={handleBrandDelete}
      />

      <QuickAddCategoryModal
        isOpen={showSizeModal}
        onClose={() => setShowSizeModal(false)}
        categoryType="size"
        onSuccess={handleSizeCreated}
        createFunction={createSize}
        existingItems={sizes}
        deleteFunction={deleteSize}
        onDelete={handleSizeDelete}
      />

      <QuickAddCategoryModal
        isOpen={showUseCaseModal}
        onClose={() => setShowUseCaseModal(false)}
        categoryType="useCase"
        onSuccess={handleUseCaseCreated}
        createFunction={createUseCase}
        existingItems={useCases}
        deleteFunction={deleteUseCase}
        onDelete={handleUseCaseDelete}
      />

      <QuickAddCategoryModal
        isOpen={showTileTypeModal}
        onClose={() => setShowTileTypeModal(false)}
        categoryType="tileType"
        onSuccess={handleTileTypeCreated}
        createFunction={createTileType}
        existingItems={tileTypes}
        deleteFunction={deleteTileType}
        onDelete={handleTileTypeDelete}
      />
    </div>
  );
};

export default ProductFormPage;
